// src/pages/Dashboard/Home.js
import React, {
  useEffect,
  useState,
  useContext,
  useRef,
  useCallback,
} from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack,
  TextField,
  Pagination, // Agregar esta línea
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close"; // Importar CloseIcon
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BarChartIcon from "@mui/icons-material/BarChart";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import MenuBookIcon from "@mui/icons-material/MenuBook"; // Para guía de implementación
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline"; // Para contratar widget
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined"; // Nuevo icono
import RefreshIcon from "@mui/icons-material/Refresh"; // Para refresh manual
import LanguageIcon from "@mui/icons-material/Language"; // Para dominios
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { AWSContext } from "../../contexts/AWSContext";
import { useCache } from "../../contexts/CacheContext";
import api from "../../services/api";
import { useTranslation } from "react-i18next";
import GeometryImage from "../../assets/left-image-widget.png";
import PlataformaSelector from "../../components/guias/PlataformaSelector";
import ActiveCampaignForm from "../../components/ActiveCampaignForm";
import { detectPlatform, PLATFORM_ROUTES } from "../../utils/detectPlatform";


const Home = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isAWS, isBig } = useContext(AWSContext);
  const { t } = useTranslation();
  const searchTimeoutRef = useRef(null);

  // Cache context
  const {
    getWidgetsCache,
    setWidgetsCache,
    getDomainVisitsCache,
    setDomainVisitsCache,
    getWidgetsCountCache,
    setWidgetsCountCache,
    getCustomerNameCache,
    setCustomerNameCache,
    getNewsCache,
    setNewsCache,
    isCacheExpired
  } = useCache();

  // Estados existentes de Home
  const [customerName, setCustomerName] = useState("");
  const [widgetsCount, setWidgetsCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Estados de Widget
  const [widgets, setWidgets] = useState([]);

  // Nuevos estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const itemsPerPage = 5; // Mostrar 5 widgets por página

  const [error, setError] = useState("");
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedWidget, setSelectedWidget] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [assignDomainModalOpen, setAssignDomainModalOpen] = useState(false);
  const [selectedWidgetForDomain, setSelectedWidgetForDomain] = useState(null);
  const [domainToAssign, setDomainToAssign] = useState("");
  const [assignDomainError, setAssignDomainError] = useState("");
  const [favicons, setFavicons] = useState({});
  const [isSearching, setIsSearching] = useState(false);
  const [fileUploadModalOpen, setFileUploadModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileUploadError, setFileUploadError] = useState("");
  const [fileUploadLoading, setFileUploadLoading] = useState(false);

  // Estado para el modal de guías (almacena el widget seleccionado, null = cerrado)
  const [guideWidget, setGuideWidget] = useState(null);
  // ID del widget cuyo botón libro está detectando plataforma
  const [detectingWidgetId, setDetectingWidgetId] = useState(null);
  const [news, setNews] = useState([]); // Nuevo estado para noticias

  // AGREGAR nuevos estados para el modal de noticia
  const [newsModalOpen, setNewsModalOpen] = useState(false);
  const [selectedNews, setSelectedNews] = useState(null);

  // Agregar nuevo estado de loading para la tabla
  const [tableLoading, setTableLoading] = useState(false);

  // Agregar nuevo estado para las visitas totales
  const [totalVisits, setTotalVisits] = useState(0);
  const [visitsLoading, setVisitsLoading] = useState(false);

  // Estado para indicar si se está actualizando en segundo plano
  const [isRefreshingInBackground, setIsRefreshingInBackground] = useState(false);

  // Efecto para cargar favicons
  useEffect(() => {
    const genericUrl =
      "https://www.google.com/s2/favicons?domain=example.com&sz=64";
    const genericImg = new Image();
    genericImg.src = genericUrl;

    genericImg.onload = () => {
      const updatedFavicons = {};
      const promises = widgets.map((widget) => {
        return new Promise((resolve) => {
          if (!widget.dominio) {
            updatedFavicons[widget.id] = GeometryImage;
            return resolve();
          }

          const widgetFavicon = new Image();
          const url = `https://www.google.com/s2/favicons?domain=${widget.dominio}&sz=64`;
          widgetFavicon.src = url;

          widgetFavicon.onload = () => {
            if (
              widgetFavicon.naturalWidth === genericImg.naturalWidth &&
              widgetFavicon.naturalHeight === genericImg.naturalHeight
            ) {
              updatedFavicons[widget.id] = GeometryImage;
            } else {
              updatedFavicons[widget.id] = url;
            }
            resolve();
          };

          widgetFavicon.onerror = () => {
            updatedFavicons[widget.id] = GeometryImage;
            resolve();
          };
        });
      });

      Promise.all(promises).then(() => {
        setFavicons(updatedFavicons);
      });
    };
  }, [widgets]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 1. MOVER fetchAllWidgetsForVisits ANTES de fetchMultiDomainVisits
  const fetchAllWidgetsForVisits = useCallback(async () => {
    try {
      const response = await api.get(`/servicios?user_id=${user.id}&all=true`);
      const allServices = response.data.data || response.data;
      // Filtrar solo servicios de tipo Widget
      const widgets = allServices.filter(service => service.service_type === "Widget");
      return widgets;
    } catch (error) {
      return [];
    }
  }, [user?.id]); // Cambiar user.id por user?.id para evitar errores

  // 2. ACTUALIZAR fetchMultiDomainVisits para incluir fetchAllWidgetsForVisits en las dependencias
  const fetchMultiDomainVisits = useCallback(async (forceRefresh = false) => {
    try {
      setVisitsLoading(true);

      // Verificar caché del CacheContext primero
      const cachedVisits = getDomainVisitsCache();
      const isExpired = isCacheExpired('domainVisits');

      // Si hay datos en caché y no han expirado (y no es refresh forzado)
      if (!forceRefresh && cachedVisits && cachedVisits.data && !isExpired) {
        setTotalVisits(cachedVisits.totalVisits || 0);
        setVisitsLoading(false);
        return;
      }

      // Get ALL widgets for visits calculation
      const allWidgets = await fetchAllWidgetsForVisits();

      // Extract domains from ALL widgets
      const domains = allWidgets
        .map((widget) => widget.dominio || widget.domain)
        .filter((domain) => domain);

      if (domains.length === 0) {
        setTotalVisits(0);
        setVisitsLoading(false);
        return;
      }

      const response = await api.post("/multi-domain-visits", {
        domains: domains,
        user_id: user?.id,
      });

      const visitData = response.data.data;
      let totalVisitsCount = 0;

      if (visitData && typeof visitData === "object") {
        totalVisitsCount = Object.values(visitData).reduce((sum, value) => {
          return sum + (typeof value === "number" ? value : 0);
        }, 0);
      } else if (typeof visitData === "number") {
        totalVisitsCount = visitData;
      }

      setTotalVisits(totalVisitsCount);

      // Guardar en caché usando CacheContext
      setDomainVisitsCache(visitData, totalVisitsCount);
    } catch (error) {

      // Si hay error, intentar usar caché aunque esté expirado
      const cachedVisits = getDomainVisitsCache();

      if (cachedVisits && cachedVisits.data) {
        setTotalVisits(cachedVisits.totalVisits || 0);
      } else {
        setTotalVisits(0);
      }
    } finally {
      setVisitsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, fetchAllWidgetsForVisits]); // ✅ Solo dependencias necesarias, funciones de caché ya no cambian

  // 3. ACTUALIZAR fetchWidgets también con user?.id
  const fetchWidgets = useCallback(
    async (page = 1, search = "", forceRefresh = false) => {
      try {
        // Verificar caché primero (solo si no hay búsqueda y es página 1)
        /* const cacheKey = `widgets-p${page}-s${search}`; */
        const cachedWidgets = getWidgetsCache();
        const isExpired = isCacheExpired('widgets');

        // Si hay datos en caché, no hay búsqueda, y no es refresh forzado
        if (!forceRefresh && !search && page === 1 && cachedWidgets && cachedWidgets.data && !isExpired) {
          setWidgets(cachedWidgets.data || []);
          setCurrentPage(cachedWidgets.pagination?.currentPage || page);
          setTotalPages(cachedWidgets.pagination?.totalPages || 1);
          setTotalItems(cachedWidgets.totalItems || 0);
          setTableLoading(false);

          // ✅ ELIMINADO: Background refresh automático que causaba peticiones infinitas
          // El refresh ahora solo ocurre manualmente con el botón de refresh
          return;
        }

        // MOSTRAR loading para todas las operaciones excepto búsqueda en tiempo real
        setTableLoading(true);

        const params = new URLSearchParams({
          user_id: user?.id,
          page: page.toString(),
          limit: itemsPerPage.toString(),
          sortBy: "creation_date",
          sortOrder: "desc",
        });

        // Agregar búsqueda si existe
        if (search && search.trim()) {
          params.append("search", search.trim());
        }

        const response = await api.get(`/servicios?${params}`);

        // Filtrar solo servicios de tipo Widget
        const allServices = response.data.data || [];
        const widgets = allServices.filter(service => service.service_type === "Widget");

        // Actualizar estados con la nueva estructura
        setWidgets(widgets);
        setCurrentPage(response.data.pagination?.currentPage || page);
        setTotalPages(response.data.pagination?.totalPages || 1);
        setTotalItems(widgets.length); // Usar la cantidad de widgets filtrados

        // Guardar en caché solo si no hay búsqueda y es página 1
        if (!search && page === 1) {
          setWidgetsCache(widgets, response.data.pagination, widgets.length);
        }
      } catch (error) {
        setError(
          t("widget.errorFetchingWidgets") || "Error al obtener widgets."
        );
        // En caso de error, limpiar los widgets
        setWidgets([]);
        setTotalPages(1);
        setTotalItems(0);
      } finally {
        // SIEMPRE quitar loading
        setTableLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.id, t, itemsPerPage] // ✅ Solo dependencias necesarias, funciones de caché ya no cambian
  );

  // 4. ACTUALIZAR el useEffect principal
  useEffect(() => {
    const fetchCustomerName = async () => {
      try {
        // Verificar caché primero
        const cachedName = getCustomerNameCache();
        const isExpired = isCacheExpired('customerName');

        if (cachedName && cachedName.data && !isExpired) {
          setCustomerName(cachedName.data);
          return;
        }

        const response = await api.get(`/customer-name/${user?.id}`);
        setCustomerName(response.data.customerName);
        setCustomerNameCache(response.data.customerName);
      } catch (error) {
      }
    };

    const fetchWidgetsCount = async () => {
      try {
        // Verificar caché primero
        const cachedCount = getWidgetsCountCache();
        const isExpired = isCacheExpired('widgetsCount');

        if (cachedCount && cachedCount.data !== null && !isExpired) {
          setWidgetsCount(cachedCount.data);
          return;
        }

        const response = await api.get(`/widgets-count/${user?.id}`);
        setWidgetsCount(response.data.widgetsCount);
        setWidgetsCountCache(response.data.widgetsCount);
      } catch (error) {
      }
    };

    const fetchNews = async () => {
      try {
        // Verificar caché primero
        const cachedNews = getNewsCache();
        const isExpired = isCacheExpired('news');

        if (cachedNews && cachedNews.data && !isExpired) {
          setNews(cachedNews.data);
          return;
        }

        const response = await api.get("/news");
        setNews(response.data || []);
        setNewsCache(response.data || []);
      } catch (error) {
      }
    };

    const fetchData = async () => {
      await Promise.all([
        fetchCustomerName(),
        fetchWidgetsCount(),
        fetchWidgets(1), // Solo cargar primera página en carga inicial
        fetchNews(),
        fetchMultiDomainVisits(), // Llamar aquí para evitar useEffect duplicado
      ]);
      setLoading(false);
    };

    // SOLO ejecutar en carga inicial del componente
    if (user?.id && loading) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, loading]); // ✅ Solo dependencias que realmente deben trigger el effect

  // Funciones de Widget
  const handleModify = (widget) => {
    // Navegar a la nueva pantalla intermedia
    navigate("/dashboard/editar-widget", {
      state: {
        service_id: widget.id,
        billing_interval_type: widget.billing_cycle_type,
      },
    });
  };

  const handleCancel = (widget) => {
    if (isAWS) {
      const awsMarketplaceUrl =
        process.env.AWS_MARKETPLACE_CANCEL_URL ||
        "https://aws.amazon.com/marketplace/pp/prodview-3yziwygxgj6do";
      window.open(awsMarketplaceUrl, "_blank");
    } else {
      setSelectedWidget(widget);
      setCancelDialogOpen(true);
    }
  };

  const confirmCancel = async () => {
    if (!selectedWidget || !selectedWidget.id) {
      setError(
        t("widget.errorCancelSubscription") ||
          "No se pudo obtener el subscription_id del widget seleccionado."
      );
      return;
    }
    try {
      await api.delete("/cancel-subscription", {
        data: { service_id: selectedWidget.id },
      });
      setCancelDialogOpen(false);
      window.location.reload();
    } catch (error) {
      setError(
        t("widget.errorCancelSubscription") || "Error al cancelar suscripción."
      );
    }
  };

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleOpenAssignDomainModal = (widget) => {
    setSelectedWidgetForDomain(widget);
    setAssignDomainModalOpen(true);
  };

  const handleCloseAssignDomainModal = () => {
    setAssignDomainModalOpen(false);
    setDomainToAssign("");
    setAssignDomainError("");
    setSelectedWidgetForDomain(null);
  };

  const handleAssignDomainSubmit = async () => {
    setAssignDomainError("");
    try {
      await api.post("/assign-domain", {
        service_id: selectedWidgetForDomain.id,
        domain: domainToAssign.trim(),
      });
      window.location.reload();
    } catch (error) {
      setAssignDomainError(
        t("widget.assignDomainError") || "Error al asignar dominio."
      );
    }
  };

  // Funciones para el modal de guías por widget
  const handleOpenGuidesModal = async (widget) => {
    const domain = widget.dominio || widget.domain;
    if (!domain) {
      // Sin dominio: abrir selector manual directamente
      setGuideWidget(widget);
      return;
    }
    setDetectingWidgetId(widget.id);
    try {
      const platform = await detectPlatform(domain);
      const route = platform ? PLATFORM_ROUTES[platform] : null;
      if (route) {
        navigate(route);
      } else {
        // No detectado → abrir modal de selección manual
        setGuideWidget(widget);
      }
    } finally {
      setDetectingWidgetId(null);
    }
  };

  const handleCloseGuidesModal = () => {
    setGuideWidget(null);
  };

  // AGREGAR estas funciones para el modal de noticia
  const handleOpenNewsModal = (newsItem) => {
    setSelectedNews(newsItem);
    setNewsModalOpen(true);
  };

  const handleCloseNewsModal = () => {
    setNewsModalOpen(false);
    setSelectedNews(null);
  };

  const handleViewAllNews = () => {
    // Placeholder for view all news functionality
    console.log("View all news clicked");
  };

  // Función para generar el código de implementación
  const generateImplementationCode = (config) => {
    const settings = {
      color: config.color || "blue",
      mode: config.mode || "light",
      iconPosition: config.iconPosition || "bottomRight",
      autoDeploy: config.autoDeploy || false,
      windowPosition: config.windowPosition || "right",
      iconType: config.iconType || "whiteRoundIcon",
      defaultLanguage: config.defaultLanguage || "es",
      iconSizeValue: config.iconSizeValue || "Mediano",
    };

    return `<script>window.onload = function () {const bmvScript = document.createElement('script');bmvScript.src= 'https://widget.bemyvega.com/build/bmvPlugin.js';bmvScript.addEventListener('load', () => {const settings = ${JSON.stringify(
      settings
    )};BmvPlugin.setConfig(settings);});document.head.appendChild(bmvScript);};</script>`;
  };

  // Función para copiar al portapapeles
  const handleCopyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      // Aquí podrías agregar una notificación de éxito
    } catch (err) {
    }
  };

  // Funciones para búsqueda y paginación
  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage); // Establecer inmediatamente la nueva página
    fetchWidgets(newPage, searchTerm);
  };

  // Función para búsqueda (puedes implementarla más tarde)
  const handleSearch = async (term) => {
    const trimmedTerm = term.trim();

    // Solo actualizar si realmente cambió el término de búsqueda
    if (trimmedTerm !== searchTerm) {
      setIsSearching(true);
      setSearchTerm(trimmedTerm);
      setCurrentPage(1); // Solo resetear página cuando cambia la búsqueda

      try {
        await fetchWidgets(1, trimmedTerm);
      } finally {
        setIsSearching(false);
      }
    }
  };

  // Agregar estado para controlar el valor del input de búsqueda
  const [searchInputValue, setSearchInputValue] = useState("");

  // Función para manejar cambios en el input de búsqueda - CORREGIDA
  const handleSearchInputChange = (event) => {
    const value = event.target.value;
    setSearchInputValue(value);

    // SOLO usar búsqueda con delay, eliminar la búsqueda inmediata
    // Limpiar el timeout anterior
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Establecer nuevo timeout
    searchTimeoutRef.current = setTimeout(() => {
      handleSearch(value);
    }, 300); // Reducido a 300ms para mejor UX
  };

  // Función para limpiar la búsqueda - MEJORADA
  const handleClearSearch = () => {
    // Limpiar timeout pendiente
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    setSearchInputValue("");
    setSearchTerm("");
    setCurrentPage(1);
    fetchWidgets(1, "");
  };

  // Función para manejar el submit del formulario de búsqueda (Enter) - MEJORADA
  const handleSearchSubmit = (event) => {
    event.preventDefault();

    // Limpiar timeout pendiente para ejecutar inmediatamente
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    handleSearch(searchInputValue);
  };

  // Función para refresh manual
  const handleRefreshData = async () => {
    setIsRefreshingInBackground(true);
    setTableLoading(true);
    try {
      await Promise.all([
        fetchWidgets(currentPage, searchTerm, true), // forceRefresh = true
        fetchMultiDomainVisits(true), // forceRefresh = true
      ]);
    } catch (error) {
    } finally {
      setIsRefreshingInBackground(false);
      setTableLoading(false);
    }
  };

  const handleOpenFileUploadModal = () => {
    setFileUploadModalOpen(true);
  };

  const handleCloseFileUploadModal = () => {
    setFileUploadModalOpen(false);
    setSelectedFile(null);
    setFileUploadError("");
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validar tipo de archivo
      const validTypes = [
        "text/csv",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];

      if (
        validTypes.includes(file.type) ||
        file.name.endsWith(".csv") ||
        file.name.endsWith(".xls") ||
        file.name.endsWith(".xlsx")
      ) {
        setSelectedFile(file);
        setFileUploadError("");
      } else {
        setFileUploadError(
          "Por favor selecciona un archivo CSV, XLS o XLSX válido."
        );
        setSelectedFile(null);
      }
    }
  };

  // ACTUALIZAR la función handleFileUpload para incluir los parámetros requeridos
  // MEJORAR la función handleFileUpload con mejor manejo de errores
  const handleFileUpload = async () => {
    if (!selectedFile) {
      setFileUploadError("Por favor selecciona un archivo.");
      return;
    }

    try {
      setFileUploadLoading(true);
      setFileUploadError("");

      // Validar tamaño del archivo
      if (selectedFile.size > 10 * 1024 * 1024) {
        throw new Error(
          "El archivo es demasiado grande. Máximo 10MB permitido."
        );
      }

      // Crear FormData
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("customer_id", user.id);
      formData.append("plan_id", "c4b97d47-3bfe-4c28-8a78-4cedec51b8ba");
      formData.append("billing_interval_type", "M");

      // ✅ MEJORAR: Configuración específica para multipart/form-data
      const response = await api.post(
        "/big-customer-bulk-registration",
        formData,
        {
          headers: {
            // ✅ IMPORTANTE: NO establecer Content-Type manualmente
            // axios lo configurará automáticamente con el boundary correcto
            // "Content-Type": "multipart/form-data", // ❌ ELIMINAR ESTA LÍNEA
            Accept: "application/json",
          },
          timeout: 300000, // 5 minutos
          maxContentLength: 10 * 1024 * 1024, // 10MB
          maxBodyLength: 10 * 1024 * 1024, // 10MB
        }
      );

      if (response.status >= 400) {
        const errorData = response.data;
        throw new Error(
          errorData.error || errorData.message || "Error al procesar el archivo"
        );
      }

      // Cerrar modal y refrescar
      handleCloseFileUploadModal();
      await fetchWidgets(1);
    } catch (error) {
      let errorMessage =
        "Error al cargar el archivo. Verifica el formato y vuelve a intentarlo.";

      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;

        if (status === 400) {
          errorMessage =
            data?.error || "Datos inválidos. Verifica el formato del archivo.";
        } else if (status === 413) {
          errorMessage =
            "El archivo es demasiado grande. Máximo 10MB permitido.";
        } else if (status === 415) {
          errorMessage =
            "Formato de archivo no soportado. Usa CSV, XLS o XLSX.";
        } else if (status >= 500) {
          errorMessage = "Error interno del servidor. Intenta más tarde.";
        } else {
          errorMessage =
            data?.message || data?.error || `Error del servidor (${status})`;
        }
      } else if (error.code === "ECONNABORTED") {
        errorMessage =
          "El archivo tardó demasiado en procesarse. Intenta con un archivo más pequeño.";
      } else {
        errorMessage = error.message || errorMessage;
      }

      setFileUploadError(errorMessage);
    } finally {
      setFileUploadLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth: "100vw",
        px: { xs: 0, md: 2.5 },
        py: { xs: 0, md: 4 },
        pb: 0,
        mr: { xs: -6 },
        pr: { xs: 0 },
      }}
    >
      {/* Cabecera como en las guías */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          padding: { xs: "12px 16px", sm: "8px 16px", md: "12px 16px" },
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          backgroundColor: "white",
          zIndex: 1000,
        }}
      >
        {/* Fila principal */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          {/* Columna izquierda: Texto */}
          <Box sx={{ 
            display: { xs: "flex", sm: "flex" }, 
            flexDirection: "column", 
            flex: 1,
            ml: { xs: "50px", sm: "50px", md: 35.5 },
          }}>
            <Typography
              variant="h5"
              sx={{
                fontSize: { xs: "18px", sm: "16px", md: "14px", lg: "20px" },
                fontWeight: "medium",
                color: "#000",
              }}
            >
              {t("home.greeting", { name: customerName })}
            </Typography>

            <Typography
              variant="subtitle1"
              sx={{
                fontSize: { xs: "12px", sm: "15px", md: "14px" },
                color: "#666",
                mt: { xs: 0, md: 0.3 },
                display: { xs: "none", sm: "none", md: "none", lg: "block" },
              }}
            >
              {t("home.subtitle")}
            </Typography>
          </Box>

          {/* Columna derecha: Botones */}
          <Box
            sx={{
              display: "flex",
              gap: { xs: 1.5, sm: 2 },
              mr: { xs: 1.5, md: 5 },
              alignItems: "center",
            }}
          >
            <Box sx={{ display: { xs: "none", sm: "none", md: "none", lg: "flex" }, gap: 0.5 }}>
              <IconButton
                onClick={handleRefreshData}
                disabled={isRefreshingInBackground}
                sx={{
                  color: "#0F52BD",
                  "&:hover": { backgroundColor: "#E3F2FD" },
                  animation: isRefreshingInBackground ? "spin 1s linear infinite" : "none",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                  width: 40,
                  height: 40,
                }}
              >
                <RefreshIcon sx={{ fontSize: "24px" }} />
              </IconButton>
            </Box>
            
            {!isAWS && (
              <>
                {/* Botón de añadir dominio - Solo icono en móviles */}
                <Box sx={{ display: { xs: "block", sm: "none" } }}>
                  <IconButton
                    onClick={isBig ? handleOpenFileUploadModal : handleOpenModal}
                    sx={{
                      color: "#0F52BD",
                      backgroundColor: "transparent",
                      borderRadius: "8px",
                      "&:hover": { backgroundColor: "#E3F2FD" },
                      width: 40,
                      height: 40,
                    }}
                  >
                    <AddCircleOutlineIcon sx={{ fontSize: "28px" }} />
                  </IconButton>
                </Box>
                
                {/* Botón de añadir dominio - Con texto en tablets/PC */}
                <Box sx={{ display: { xs: "none", sm: "block" } }}>
                  <Button
                    variant="contained"
                    startIcon={<AddCircleOutlineIcon sx={{ fontSize: { sm: "18px", md: "16px" } }} />}
                    onClick={isBig ? handleOpenFileUploadModal : handleOpenModal}
                    sx={{
                      textTransform: "none",
                      fontWeight: "medium",
                      backgroundColor: "#0F52BD",
                      fontSize: { sm: "12px", md: "12px" },
                      px: { sm: 1.5, md: 1.5 },
                      py: { sm: 0.8, md: 0.8 },
                      borderRadius: "8px",
                    }}
                  >
                    {isBig ? t("home.loadDomains") : t("home.addNewDomain")}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Spacer para empujar el contenido hacia abajo */}
      <Box
        sx={{
          height: { xs: "65px", sm: "70px", md: "100px", lg: "90px" },
          // Ajustar según el zoom
          "@media (max-width: 600px) and (min-resolution: 1.25dppx)": {
            height: "65px",
          },
          "@media (max-width: 600px) and (min-resolution: 1.5dppx)": {
            height: "90px",
          },
          "@media (min-width: 600px) and (min-resolution: 1.25dppx)": {
            height: "75px",
          },
          "@media (min-width: 900px) and (min-resolution: 1.25dppx)": {
            height: "95px",
          },
        }}
      />

      {/* Contenido principal sin margin top */}
      <Box sx={{ ml: { xs: -6, md: 0 } }}>
        <Box
          sx={{ paddingLeft: { xs: 6, md: 2 }, paddingRight: { xs: 0, md: 4 } }}
        >
          {/* Las 3 tarjetas principales - ahora más altas */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Tarjeta de Dominios Contratados */}
            <Grid item xs={12} md={12} lg={4}>
              <Paper
                elevation={0}
                sx={{
                  pt: 4,
                  pl: 4,
                  pr: 3,
                  pb: 3,
                  position: "relative",
                  height: { xs: "220px", md: "240px", lg: "280px" },
                  width: { xs: "100%", lg: "483px" },
                  maxWidth: "100%",
                  boxSizing: "border-box",
                  boxShadow: "0px 0px 10px 2px rgba(98, 113, 137, 0.46)",
                  borderRadius: 2.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <SearchIcon
                    sx={{
                      fontSize: 28,
                      fontWeight: "bold",
                      mr: 1,
                      color: "#000",
                    }}
                  />
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ fontSize: "18px", color: "#000" }}
                  >
                    {t("home.widgetTitle")}
                  </Typography>
                </Box>
                <Typography
                  variant="body1"
                  gutterBottom
                  sx={{ 
                    fontSize: "14px", 
                    color: "#4D4D4D", 
                    mb: 1,
                    maxWidth: { sm: "70ch", md: "70ch", lg: "100%" },
                  }}
                >
                  {t("home.widgetDescription")}
                </Typography>
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 16,
                    left: 24,
                    display: "flex",
                    alignItems: "baseline",
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: "32px",
                      color: "#4F4F4F",
                      fontWeight: "bold",
                      lineHeight: 1,
                    }}
                  >
                    {widgetsCount}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ fontSize: "14px", color: "#4D4D4D" }}
                  >
                    {t("home.contractedDomains").toLowerCase()}
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Tarjeta de Uso del Widget - Actualizada */}
            <Grid item xs={12} md={12} lg={4}>
              <Paper
                elevation={0}
                sx={{
                  pt: 4,
                  pl: 4,
                  pr: 3,
                  pb: 3,
                  height: { xs: "260px", md: "240px", lg: "280px" },
                  width: { xs: "100%", lg: "483px" },
                  maxWidth: "100%",
                  boxSizing: "border-box",
                  boxShadow: "0px 0px 10px 2px rgba(98, 113, 137, 0.46)",
                  borderRadius: 2.5,
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <TrendingUpIcon sx={{ fontSize: 28, mr: 1, color: "#000" }} />
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ fontSize: "18px", color: "#000" }}
                  >
                    {t("home.widgetUsage")}
                  </Typography>
                </Box>
                <Typography
                  variant="body1"
                  sx={{ 
                    fontSize: "14px", 
                    color: "#4D4D4D", 
                    mb: 1,
                    maxWidth: { sm: "70ch", md: "70ch", lg: "100%" },
                  }}
                >
                  {t("home.widgetUsageDescription")}
                  <br />
                  {t("home.widgetUsageDetails")}
                </Typography>

                {/* Mostrar visitas totales */}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 16,
                    left: 24,
                    display: "flex",
                    alignItems: "baseline",
                    gap: 1,
                  }}
                >
                  {visitsLoading ? (
                    <CircularProgress size={24} sx={{ color: "#0F52BD" }} />
                  ) : (
                    <>
                      <Typography
                        variant="h2"
                        sx={{
                          fontSize: "32px",
                          color: "#4F4F4F",
                          fontWeight: "bold",
                          lineHeight: 1,
                        }}
                      >
                        {totalVisits.toLocaleString()}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ fontSize: "14px", color: "#4D4D4D" }}
                      >
                        {t("home.totalOpenings").toLowerCase()}
                      </Typography>
                    </>
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* Tarjeta de Novedades - ACTUALIZADA */}
            <Grid item xs={12} md={12} lg={4}>
              <Paper
                elevation={0}
                sx={{
                  pt: 4,
                  pl: 4,
                  pr: 3,
                  pb: 3,
                  height: { xs: "auto", md: "240px", lg: "280px" },
                  minHeight: "220px",
                  width: { xs: "100%", lg: "483px" },
                  maxWidth: "100%",
                  boxSizing: "border-box",
                  boxShadow: "0px 0px 10px 2px rgba(98, 113, 137, 0.46)",
                  borderRadius: 2.5,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Header con icono y enlace "Ver todas" */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <NotificationsOutlinedIcon
                      sx={{ fontSize: 28, mr: 1, color: "#000" }}
                    />
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{ fontSize: "18px", color: "#000" }}
                    >
                      {t("home.latestNews")}
                    </Typography>
                  </Box>
                  <Button
                    onClick={handleViewAllNews}
                    sx={{
                      textTransform: "none",
                      fontSize: "14px",
                      color: "#0F52BD",
                      fontWeight: "medium",
                      padding: 0,
                      minWidth: "auto",
                      "&:hover": {
                        backgroundColor: "transparent",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    {t("home.viewAll")} &gt;
                  </Button>
                </Box>

                {/* Contenido de noticias */}
                <Box sx={{ flex: 1, overflow: "hidden" }}>
                  {news.length > 0 ? (
                    <Stack spacing={1.5}>
                      {news.slice(0, 3).map((item, index) => (
                        <Box
                          key={item.new_id}
                          sx={{
                            borderBottom:
                              index < 2
                                ? "1px solid #E0E0E0"
                                : "none",
                            pb: index < 2 ? 1.5 : 0,
                          }}
                        >
                          {/* Contenedor principal de cada noticia */}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                            }}
                          >
                            {/* Contenido de la noticia */}
                            <Box sx={{ flex: 1, pr: 1 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "14px",
                                  color: "#000",
                                  fontWeight: "medium",
                                  mb: 0.2,
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  lineHeight: 1.3,
                                }}
                              >
                                {item.summary}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "12px",
                                  color: "#666",
                                  display: "block",
                                }}
                              >
                                {item.title}
                              </Typography>
                            </Box>

                            {/* Enlace "Saber más" - ACTUALIZADO */}
                            <Button
                              size="small"
                              sx={{
                                textTransform: "none",
                                fontSize: "12px",
                                color: "#0F52BD",
                                fontWeight: "medium",
                                padding: "2px 8px",
                                minWidth: "auto",
                                flexShrink: 0,
                                "&:hover": {
                                  backgroundColor: "transparent",
                                  textDecoration: "underline",
                                },
                              }}
                              onClick={() => handleOpenNewsModal(item)} // ACTUALIZADO
                            >
                              {t("home.learnMore")}
                            </Button>
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{ fontSize: "14px", color: "#4D4D4D" }}
                    >
                      {t("home.noNewsAvailable")}
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Tarjeta del listado de widgets - Con el mismo contenedor que las tarjetas de arriba */}
        <Box
          sx={{ paddingLeft: { xs: 6, md: 2 }, paddingRight: { xs: 0, md: 4 } }}
        >
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={12} lg={12}>
              <Paper
                elevation={0}
                sx={{
                  pt: 4,
                  pl: 4,
                  pr: 3,
                  pb: 3,
                  position: "relative",
                  width: "100%",
                  maxWidth: "100%",
                  boxSizing: "border-box",
                  boxShadow: "0px 0px 10px 2px rgba(98, 113, 137, 0.46)",
                  borderRadius: 2.5,
                  overflow: "hidden",
                }}
              >
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Título y Barra de búsqueda - Alineados horizontalmente */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              {/* Título */}
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <LanguageIcon
                  sx={{
                    fontSize: 28,
                    fontWeight: "bold",
                    mr: 1,
                    color: "#000",
                  }}
                />
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ fontSize: "18px", color: "#000" }}
                >
                  {t("Dominios")}
                </Typography>
              </Box>

              {/* Barra de búsqueda */}
              <Box
                component="form"
                onSubmit={handleSearchSubmit}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <TextField
                  size="small"
                  placeholder={t("home.searchDomain")}
                  value={searchInputValue}
                  onChange={handleSearchInputChange}
                  disabled={false}
                  autoComplete="off"
                  sx={{
                    width: { xs: "140px", sm: "220px", md: "180px", lg: "270px" },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      fontSize: "14px",
                      backgroundColor: isSearching ? "#f5f5f5" : "white",
                      opacity: isSearching ? 0.7 : 1,
                    },
                  }}
                  InputProps={{
                    startAdornment: isSearching ? (
                      <CircularProgress
                        size={16}
                        sx={{ color: "#999", mr: 1 }}
                      />
                    ) : (
                      <SearchIcon sx={{ color: "#999", mr: 1 }} />
                    ),
                    endAdornment: searchInputValue && (
                      <IconButton
                        size="small"
                        onClick={handleClearSearch}
                        type="button"
                        disabled={isSearching}
                        sx={{
                          color: "#999",
                          "&:hover": { color: "#666" },
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    ),
                  }}
                />
              </Box>
            </Box>

            {/* Contenedor responsive */}
            <Box sx={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
              {/* Contenedor de tabla unificado - RESPONSIVE */}
              <Box sx={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
                {/* Cabeceras - RESPONSIVE - 4 columnas */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "1.5fr 1fr 0.8fr",
                      md: "200px 150px 1fr 120px",
                      lg: "2fr 1fr 2.5fr 1.5fr",
                    },
                    gap: "105px",
                    py: 1,
                    borderBottom: "2px solid #E0E0E0",
                    mb: 2,
                    alignItems: "flex-start",
                    width: "100%",
                    maxWidth: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  {/* Celda 1: Dominio - OCULTAR EN MÓVIL */}
                  <Box
                    sx={{
                      display: { xs: "none", sm: "flex" },
                      alignItems: "flex-start",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "bold",
                        fontSize: "16px",
                        color: "#000",
                      }}
                    >
                      {t("Dominio")}
                    </Typography>
                  </Box>

                  {/* Celda 2: Plan - OCULTAR EN MÓVIL */}
                  <Box sx={{ display: { xs: "none", sm: "block" }, textAlign: "center" }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "bold",
                        fontSize: "16px",
                        color: "#000",
                      }}
                    >
                      Plan de precios
                    </Typography>
                  </Box>

                  {/* Celda 3: Código - OCULTAR EN MÓVIL Y TABLET */}
                  <Box
                    sx={{
                      display: { xs: "none", md: "flex" },
                      alignItems: "flex-start",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "bold",
                        fontSize: "16px",
                        color: "#000",
                        flex: 1,
                      }}
                    >
                      {t("home.implementationGuide")}
                    </Typography>
                  </Box>

                  {/* Celda 4: Header vacío para acciones */}
                  <Box sx={{ display: { xs: "none", md: "block" } }}></Box>
                </Box>

                {/* CONTENIDO - RESPONSIVE */}
                {tableLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: "300px",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <CircularProgress size={40} sx={{ color: "#0F52BD" }} />
                    <Typography variant="body2" sx={{ color: "#666" }}>
                      Cargando dominios...
                    </Typography>
                  </Box>
                ) : widgets.length > 0 ? (
                  <Stack spacing={0}>
                    {widgets.map((widget) => {
                      const implementationCode = generateImplementationCode(
                        widget.widget_configuration || {}
                      );

                      return (
                        <Box
                          key={widget.id}
                          sx={{
                            display: { xs: "block", sm: "grid", md: "grid" },
                            gridTemplateColumns: {
                              sm: "1.5fr 1fr 0.8fr",
                              md: "200px 150px 1fr 120px",
                              lg: "2fr 1fr 2.5fr 1.5fr",
                            },
                            gap: { sm: 1, md: "33px" },
                            py: 2,
                            alignItems: "center",
                            borderBottom: "1px solid #E0E0E0",
                            px: { xs: 0, sm: 0 },
                          }}
                        >
                          {/* VISTA MÓVIL - Stacked Layout */}
                          <Box
                            sx={{
                              display: { xs: "block", sm: "none" },
                              p: 2,
                              border: "1px solid #F0F0F0",
                              borderRadius: "8px",
                              mb: 2,
                              backgroundColor: "#FAFAFA",
                            }}
                          >
                            {/* Dominio en móvil */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                mb: 2,
                              }}
                            >
                              <Box
                                sx={{
                                  width: "32px",
                                  height: "32px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <Box
                                  component="img"
                                  src={
                                    favicons[widget.id] ||
                                    `https://www.google.com/s2/favicons?domain=${
                                      widget.dominio || widget.domain
                                    }&sz=64`
                                  }
                                  alt={widget.nombre}
                                  sx={{
                                    width: "32px",
                                    height: "32px",
                                    borderRadius: "6px",
                                  }}
                                />
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontWeight: "bold",
                                    fontSize: "16px",
                                    mb: 0.5,
                                  }}
                                >
                                  {widget.dominio ||
                                    widget.domain ||
                                    (isAWS && (
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() =>
                                          handleOpenAssignDomainModal(widget)
                                        }
                                        sx={{
                                          textTransform: "none",
                                          fontSize: "12px",
                                        }}
                                      >
                                        {t("widget.assignDomainButton")}
                                      </Button>
                                    ))}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ fontSize: "14px", color: "#666" }}
                                >
                                  {widget.current_plan?.plan_name || "N/A"}
                                </Typography>
                              </Box>
                            </Box>

                            {/* Código en móvil - Expandible */}
                            <Box sx={{ mb: 2 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: "medium",
                                  fontSize: "14px",
                                  mb: 1,
                                  color: "#333",
                                }}
                              >
                                Código de implementación:
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  backgroundColor: "#fff",
                                  border: "1px solid #E0E0E0",
                                  borderRadius: "6px",
                                  padding: "8px",
                                  fontSize: "11px",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "11px",
                                    color: "#333",
                                    fontFamily: "monospace",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    flex: 1,
                                    pr: 1,
                                  }}
                                >
                                  {implementationCode}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleCopyCode(implementationCode)
                                  }
                                  sx={{
                                    color: "#999",
                                    padding: "4px",
                                    "&:hover": { color: "#000" },
                                  }}
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>

                            {/* Acciones en móvil */}
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-around",
                                pt: 1,
                                borderTop: "1px solid #E0E0E0",
                              }}
                            >
                              <IconButton
                                onClick={() =>
                                  navigate(
                                    `/dashboard/widgets/${
                                      widget.dominio || widget.domain
                                    }/estadisticas`
                                  )
                                }
                                sx={{
                                  color: "#999",
                                  "&:hover": { color: "#000" },
                                  flexDirection: "column",
                                  fontSize: "12px",
                                }}
                              >
                                <BarChartIcon fontSize="small" />
                                <Typography
                                  variant="caption"
                                  sx={{ fontSize: "10px", mt: 0.5 }}
                                >
                                  {t("home.statistics")}
                                </Typography>
                              </IconButton>

                              <IconButton
                                onClick={() => handleModify(widget)}
                                sx={{
                                  color: "#999",
                                  "&:hover": { color: "#0F52BD" },
                                  flexDirection: "column",
                                }}
                              >
                                <DriveFileRenameOutlineIcon fontSize="small" />
                                <Typography
                                  variant="caption"
                                  sx={{ fontSize: "10px", mt: 0.5 }}
                                >
                                  {t("home.modify")}
                                </Typography>
                              </IconButton>

                              <IconButton
                                onClick={() => handleCancel(widget)}
                                sx={{
                                  color: "#999",
                                  "&:hover": { color: "#FF0000" },
                                  flexDirection: "column",
                                }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                                <Typography
                                  variant="caption"
                                  sx={{ fontSize: "10px", mt: 0.5 }}
                                >
                                  {t("home.delete")}
                                </Typography>
                              </IconButton>

                              <IconButton
                                onClick={() => handleOpenGuidesModal(widget)}
                                disabled={detectingWidgetId === widget.id}
                                sx={{
                                  color: "#999",
                                  "&:hover": { color: "#0F52BD" },
                                  flexDirection: "column",
                                }}
                              >
                                {detectingWidgetId === widget.id
                                  ? <CircularProgress size={16} />
                                  : <MenuBookIcon fontSize="small" />}
                                <Typography
                                  variant="caption"
                                  sx={{ fontSize: "10px", mt: 0.5 }}
                                >
                                  {t("home.implementationGuide")}
                                </Typography>
                              </IconButton>
                            </Box>
                          </Box>

                          {/* VISTA TABLET Y DESKTOP - Grid Layout */}
                          <>
                            {/* Celda 1: Dominio - TABLET Y DESKTOP */}
                            <Box
                              sx={{
                                display: { xs: "none", sm: "flex" },
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <Box
                                sx={{
                                  width: "40px",
                                  height: "40px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <Box
                                  component="img"
                                  src={
                                    favicons[widget.id] ||
                                    `https://www.google.com/s2/favicons?domain=${
                                      widget.dominio || widget.domain
                                    }&sz=64`
                                  }
                                  alt={widget.nombre}
                                  sx={{
                                    width: "40px",
                                    height: "40px",
                                    borderRadius: "8px",
                                  }}
                                />
                              </Box>
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: "medium", fontSize: "14px" }}
                              >
                                {widget.dominio ||
                                  widget.domain ||
                                  (isAWS && (
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={() =>
                                        handleOpenAssignDomainModal(widget)
                                      }
                                      sx={{
                                        textTransform: "none",
                                        fontSize: "12px",
                                      }}
                                    >
                                      {t("widget.assignDomainButton")}
                                    </Button>
                                  ))}
                              </Typography>
                            </Box>

                            {/* Celda 2: Plan - TABLET Y DESKTOP */}
                            <Box
                              sx={{
                                display: { xs: "none", sm: "block" },
                                textAlign: "center",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: "medium", fontSize: "14px" }}
                              >
                                {widget.current_plan?.plan_name || "N/A"}
                              </Typography>
                            </Box>

                            {/* Celda 3: Código - SOLO DESKTOP */}
                            <Box
                              sx={{
                                display: { xs: "none", md: "block" },
                                minWidth: 0,
                                overflow: "hidden",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  backgroundColor: "#F8F9FA",
                                  border: "1px solid #DEE2E6",
                                  borderRadius: "6px",
                                  padding: "6px 12px",
                                  width: "100%",
                                  maxWidth: "400px",
                                  boxSizing: "border-box",
                                  position: "relative",
                                  height: "36px",
                                  overflow: "hidden",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "12px",
                                    color: "#495057",
                                    fontFamily: "monospace",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    flex: 1,
                                    pr: "32px",
                                  }}
                                >
                                  {implementationCode}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleCopyCode(implementationCode)
                                  }
                                  sx={{
                                    color: "#6C757D",
                                    padding: "4px",
                                    position: "absolute",
                                    right: "4px",
                                    "&:hover": { 
                                      color: "#495057",
                                      backgroundColor: "#E9ECEF",
                                    },
                                  }}
                                >
                                  <ContentCopyIcon sx={{ fontSize: "18px" }} />
                                </IconButton>
                              </Box>
                            </Box>

                            {/* Celda 4: Acciones - SOLO DESKTOP */}
                            <Box
                              sx={{
                                display: { xs: "none", md: "flex" },
                                justifyContent: "center",
                                gap: 1,
                              }}
                            >
                              <IconButton
                                onClick={() =>
                                  navigate(
                                    `/dashboard/widgets/${
                                      widget.dominio || widget.domain
                                    }/estadisticas`
                                  )
                                }
                                sx={{
                                  color: "#999",
                                  "&:hover": { color: "#000" },
                                }}
                              >
                                <BarChartIcon />
                              </IconButton>

                              <IconButton
                                onClick={() => handleModify(widget)}
                                sx={{
                                  color: "#999",
                                  "&:hover": { color: "#0F52BD" },
                                }}
                              >
                                <DriveFileRenameOutlineIcon />
                              </IconButton>

                              <IconButton
                                onClick={() => handleCancel(widget)}
                                sx={{
                                  color: "#999",
                                  "&:hover": { color: "#FF0000" },
                                }}
                              >
                                <DeleteOutlineIcon />
                              </IconButton>

                              <IconButton
                                onClick={() => handleOpenGuidesModal(widget)}
                                disabled={detectingWidgetId === widget.id}
                                title={t("home.implementationGuide")}
                                sx={{
                                  color: "#999",
                                  "&:hover": { color: "#0F52BD" },
                                }}
                              >
                                {detectingWidgetId === widget.id
                                  ? <CircularProgress size={20} />
                                  : <MenuBookIcon />}
                              </IconButton>
                            </Box>
                          </>
                        </Box>
                      );
                    })}
                  </Stack>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: "200px",
                    }}
                  >
                    <Typography
                      sx={{
                        textAlign: "center",
                        color: "#666",
                        fontSize: "16px",
                      }}
                    >
                      {searchTerm
                        ? `No se encontraron dominios que coincidan con "${searchTerm}"`
                        : t("widget.noWidgets")}
                    </Typography>
                  </Box>
                )}

                {/* Paginación responsive */}
                {totalPages > 1 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      pt: 2,
                      mt: 2,
                      borderTop: "1px solid #E0E0E0",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: { xs: 2, sm: 0 },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        fontSize: "14px",
                        textAlign: { xs: "center", sm: "left" },
                      }}
                    >
                      {tableLoading
                        ? "Cargando..."
                        : `Mostrando ${
                            (currentPage - 1) * itemsPerPage + 1
                          } - ${Math.min(
                            currentPage * itemsPerPage,
                            totalItems
                          )} de ${totalItems} dominios`}
                    </Typography>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      color="primary"
                      shape="rounded"
                      showFirstButton={false}
                      showLastButton={false}
                      disabled={tableLoading}
                      size="small"
                      sx={{
                        "& .MuiPaginationItem-root": {
                          fontSize: "14px",
                          minWidth: "32px",
                          height: "32px",
                        },
                        "& .Mui-selected": {
                          backgroundColor: "#0F52BD !important",
                          color: "white",
                        },
                        "& .MuiPaginationItem-root:hover": {
                          backgroundColor: "#E3F2FD",
                        },
                        "& .MuiPaginationItem-previousNext": {
                          border: "1px solid #E0E0E0",
                        },
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </Paper>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Modales */}
      <Box>
        {/* Modal de Guías de Implementación - por widget */}
        <Dialog
            open={!!guideWidget}
            onClose={handleCloseGuidesModal}
            fullWidth
            maxWidth="lg"
            PaperProps={{
              sx: {
                borderRadius: 3,
                p: 0,
                m: 0,
                overflow: "hidden",
                "& .MuiDialogContent-root": {
                  padding: 0,
                  margin: 0,
                },
              },
            }}
          >
            <DialogContent
              sx={{
                padding: 0,
                margin: 0,
                "&:first-of-type": {
                  paddingTop: 0,
                },
              }}
            >
              <PlataformaSelector />
            </DialogContent>
          </Dialog>
          {/* Diálogos (mantener todos los diálogos existentes) */}
          <Dialog
            open={cancelDialogOpen}
            onClose={() => setCancelDialogOpen(false)}
          >
            <DialogTitle>{t("widget.cancelSubscriptionTitle")}</DialogTitle>
            <DialogContent>
              <Typography>{t("widget.cancelSubscriptionMessage")}</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCancelDialogOpen(false)}>
                {t("widget.cancelButton")}
              </Button>
              <Button
                onClick={confirmCancel}
                variant="contained"
                color="secondary"
              >
                {t("widget.confirmCancelButton")}
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={modalOpen}
            onClose={handleCloseModal}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle sx={{ fontWeight: "medium", fontSize: "24px" }}>
              {t("widget.subscribeWidget")}
            </DialogTitle>
            <DialogContent sx={{ padding: "25px" }}>
              <ActiveCampaignForm open={modalOpen} />
            </DialogContent>
            <DialogActions sx={{ paddingRight: "25px", paddingBottom: "25px" }}>
              <Button
                onClick={handleCloseModal}
                sx={{
                  textTransform: "none",
                  fontSize: "16px",
                  fontWeight: "regular",
                }}
              >
                {t("widget.cancelButton")}
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={assignDomainModalOpen}
            onClose={handleCloseAssignDomainModal}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle sx={{ fontWeight: "medium", fontSize: "24px" }}>
              {t("widget.assignDomainModalTitle")}
            </DialogTitle>
            <DialogContent>
              {assignDomainError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {assignDomainError}
                </Alert>
              )}
              <Box sx={{ mt: 1 }}>
                <Typography
                  sx={{ mb: 1, fontWeight: "medium", fontSize: "14px" }}
                >
                  {t("widget.domainInputLabel")}
                </Typography>
                <TextField
                  fullWidth
                  label={t("widget.domainInputPlaceholder")}
                  value={domainToAssign}
                  onChange={(e) => setDomainToAssign(e.target.value)}
                  sx={{ backgroundColor: "#F1F1F1" }}
                  margin="dense"
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ paddingRight: "25px", paddingBottom: "25px" }}>
              <Button
                onClick={handleCloseAssignDomainModal}
                sx={{ textTransform: "none", fontSize: "16px" }}
              >
                {t("widget.cancelButton")}
              </Button>
              <Button
                onClick={handleAssignDomainSubmit}
                variant="contained"
                color="primary"
                sx={{ textTransform: "none", fontSize: "16px" }}
              >
                {t("widget.assignDomainButton")}
              </Button>
            </DialogActions>
          </Dialog>
          {/* AGREGAR el nuevo Modal de Noticia al final, después de los otros modales */}
          {/* Modal de Detalle de Noticia - ACTUALIZADO */}
          <Dialog
            open={newsModalOpen}
            onClose={handleCloseNewsModal}
            fullWidth
            maxWidth="md"
            PaperProps={{
              sx: {
                borderRadius: 3,
                maxHeight: "80vh",
              },
            }}
          >
            <DialogTitle
              sx={{
                fontWeight: "bold",
                fontSize: "24px",
                color: "#000",
                borderBottom: "1px solid #E0E0E0",
                pb: 2,
                pr: 4, // Espacio para el botón de cerrar si lo hubiera
              }}
            >
              {selectedNews?.title || "Noticia"}
            </DialogTitle>

            <DialogContent
              sx={{
                padding: "24px",
                "&:first-of-type": {
                  paddingTop: "24px",
                },
              }}
            >
              {selectedNews && (
                <Box>
                  {/* Información de cabecera: Fecha y tipo de servicio */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      mb: 3,
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    {/* Fecha de publicación */}
                    {selectedNews.publish_date && (
                      <Box
                        sx={{
                          backgroundColor: "#F5F5F5",
                          padding: "6px 12px",
                          borderRadius: "6px",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "12px",
                            color: "#666",
                            fontWeight: "medium",
                          }}
                        >
                          📅{" "}
                          {new Date(
                            selectedNews.publish_date
                          ).toLocaleDateString("es-ES", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                        </Typography>
                      </Box>
                    )}

                    {/* Tipo de servicio */}
                    {selectedNews.service_type && (
                      <Box
                        sx={{
                          backgroundColor: "#E3F2FD",
                          padding: "6px 12px",
                          borderRadius: "6px",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "12px",
                            color: "#0F52BD",
                            fontWeight: "medium",
                          }}
                        >
                          🔧 {selectedNews.service_type}
                        </Typography>
                      </Box>
                    )}

                    {/* Estado */}
                    {selectedNews.status !== undefined && (
                      <Box
                        sx={{
                          backgroundColor: selectedNews.status
                            ? "#E8F5E8"
                            : "#FFF3E0",
                          padding: "6px 12px",
                          borderRadius: "6px",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "12px",
                            color: selectedNews.status ? "#2E7D32" : "#F57C00",
                            fontWeight: "medium",
                          }}
                        >
                          {selectedNews.status ? "✅ Activo" : "⏸️ Inactivo"}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Resumen/Summary destacado */}
                  <Box
                    sx={{
                      backgroundColor: "#F8F9FA",
                      padding: "16px",
                      borderRadius: "8px",
                      borderLeft: "4px solid #0F52BD",
                      mb: 3,
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: "16px",
                        color: "#333",
                        fontWeight: "medium",
                        lineHeight: 1.5,
                        fontStyle: "italic",
                      }}
                    >
                      "{selectedNews.summary}"
                    </Typography>
                  </Box>

                  {/* Lista de descripciones */}
                  {selectedNews.description &&
                    Array.isArray(selectedNews.description) &&
                    selectedNews.description.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontSize: "18px",
                            color: "#333",
                            fontWeight: "bold",
                            mb: 2,
                          }}
                        >
                          Detalles de la actualización:
                        </Typography>
                        <Box
                          component="ul"
                          sx={{
                            pl: 2,
                            m: 0,
                            "& li": {
                              mb: 1,
                              color: "#555",
                              fontSize: "14px",
                              lineHeight: 1.6,
                            },
                          }}
                        >
                          {selectedNews.description.map((item, index) => (
                            <Box component="li" key={index}>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontSize: "14px",
                                  color: "#555",
                                  lineHeight: 1.6,
                                }}
                              >
                                {item}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                  {/* Información adicional si existe */}
                  {selectedNews.content && (
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontSize: "18px",
                          color: "#333",
                          fontWeight: "bold",
                          mb: 2,
                        }}
                      >
                        Información adicional:
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: "14px",
                          color: "#555",
                          lineHeight: 1.6,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {selectedNews.content}
                      </Typography>
                    </Box>
                  )}

                  {/* URL si existe */}
                  {selectedNews.url && (
                    <Box sx={{ mt: 3, pt: 2, borderTop: "1px solid #E0E0E0" }}>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "14px", color: "#666", mb: 1 }}
                      >
                        🔗 Más información:
                      </Typography>
                      <Button
                        href={selectedNews.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          textTransform: "none",
                          fontSize: "14px",
                          color: "#0F52BD",
                          fontWeight: "medium",
                          padding: 0,
                          minWidth: "auto",
                          "&:hover": {
                            backgroundColor: "transparent",
                            textDecoration: "underline",
                          },
                        }}
                      >
                        {selectedNews.url} 🔗
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </DialogContent>

            <DialogActions
              sx={{
                padding: "16px 24px",
                borderTop: "1px solid #E0E0E0",
                backgroundColor: "#FAFAFA",
              }}
            >
              <Button
                onClick={handleCloseNewsModal}
                variant="contained"
                sx={{
                  textTransform: "none",
                  fontSize: "16px",
                  backgroundColor: "#0F52BD",
                  px: 4,
                  py: 1.5,
                  borderRadius: "8px",
                  fontWeight: "medium",
                  "&:hover": {
                    backgroundColor: "#0D47A1",
                  },
                }}
              >
                Cerrar
              </Button>
            </DialogActions>
          </Dialog>
          {/* Modal de Carga de Archivos para rol Big */}
          <Dialog
            open={fileUploadModalOpen}
            onClose={handleCloseFileUploadModal}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle sx={{ fontWeight: "medium", fontSize: "24px" }}>
              Cargar Dominios
            </DialogTitle>
            <DialogContent sx={{ padding: "25px" }}>
              {fileUploadError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {fileUploadError}
                </Alert>
              )}
              <Box sx={{ mt: 1 }}>
                <Typography
                  sx={{ mb: 2, fontWeight: "medium", fontSize: "16px" }}
                >
                  Selecciona un archivo CSV, XLS o XLSX con los dominios a
                  cargar:
                </Typography>

                {/* MEJORAR: Caja de drop más grande y visible */}
                <Box
                  sx={{
                    border: "2px dashed #C0C0C0", // Líneas más visibles
                    borderRadius: "12px", // Bordes más redondeados
                    padding: "40px 20px", // Más padding vertical
                    textAlign: "center",
                    backgroundColor: "#FAFAFA",
                    cursor: "pointer",
                    width: "100%", // ASEGURAR que ocupe todo el ancho
                    minHeight: "120px", // Altura mínima
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    transition: "all 0.2s ease", // Animación suave
                    "&:hover": {
                      borderColor: "#0F52BD",
                      backgroundColor: "#F0F8FF",
                      transform: "translateY(-2px)", // Efecto hover
                    },
                    "&:active": {
                      transform: "translateY(0px)",
                    },
                  }}
                  component="label"
                >
                  <input
                    type="file"
                    accept=".csv,.xls,.xlsx"
                    onChange={handleFileSelect}
                    style={{ display: "none" }}
                  />

                  {/* MEJORAR: Iconos y texto más grandes */}
                  <Box
                    sx={{
                      fontSize: "48px",
                      mb: 2,
                      color: selectedFile ? "#4CAF50" : "#C0C0C0",
                      transition: "color 0.2s ease",
                    }}
                  >
                    {selectedFile ? "📄" : "📁"}
                  </Box>

                  <Typography
                    variant="body1"
                    sx={{
                      mb: 1,
                      color: selectedFile ? "#2E7D32" : "#666",
                      fontSize: "16px",
                      fontWeight: "medium",
                    }}
                  >
                    {selectedFile
                      ? `✓ ${selectedFile.name}`
                      : "Haz clic aquí para seleccionar un archivo"}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ color: "#999", fontSize: "14px" }}
                  >
                    Formatos soportados: CSV, XLS, XLSX
                  </Typography>

                  {/* AGREGAR: Información adicional sobre límites */}
                  <Typography
                    variant="caption"
                    sx={{ color: "#999", fontSize: "12px", mt: 1 }}
                  >
                    Tamaño máximo: 10MB
                  </Typography>
                </Box>

                {/* MEJORAR: Información del archivo seleccionado */}
                {selectedFile && (
                  <Box
                    sx={{
                      mt: 3,
                      p: 3,
                      backgroundColor: "#E8F5E8",
                      borderRadius: "12px",
                      border: "1px solid #C8E6C9",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: "#2E7D32",
                        fontSize: "16px",
                        fontWeight: "bold",
                        mb: 1,
                      }}
                    >
                      ✓ Archivo seleccionado
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: "#2E7D32", fontSize: "14px" }}
                      >
                        📄 Nombre: {selectedFile.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#666", fontSize: "12px" }}
                      >
                        📊 Tamaño: {(selectedFile.size / 1024).toFixed(2)} KB
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#666", fontSize: "12px" }}
                      >
                        🗂️ Tipo:{" "}
                        {selectedFile.type || "Detectado automáticamente"}
                      </Typography>
                    </Box>

                    {/* AGREGAR: Botón para cambiar archivo */}
                    <Button
                      size="small"
                      sx={{
                        mt: 2,
                        textTransform: "none",
                        fontSize: "12px",
                        color: "#0F52BD",
                        fontWeight: "medium",
                        padding: "4px 8px",
                        minWidth: "auto",
                        "&:hover": {
                          backgroundColor: "transparent",
                          textDecoration: "underline",
                        },
                      }}
                      component="label"
                    >
                      <input
                        type="file"
                        accept=".csv,.xls,.xlsx"
                        onChange={handleFileSelect}
                        style={{ display: "none" }}
                      />
                      Cambiar archivo
                    </Button>
                  </Box>
                )}
              </Box>
            </DialogContent>
            <DialogActions sx={{ paddingRight: "25px", paddingBottom: "25px" }}>
              <Button
                onClick={handleCloseFileUploadModal}
                disabled={fileUploadLoading}
                sx={{
                  textTransform: "none",
                  fontSize: "16px",
                  fontWeight: "regular",
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleFileUpload}
                variant="contained"
                color="primary"
                disabled={fileUploadLoading || !selectedFile}
                sx={{
                  textTransform: "none",
                  fontSize: "16px",
                  fontWeight: "regular",
                }}
              >
                {fileUploadLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  "Cargar Dominios"
                )}
              </Button>
            </DialogActions>
          </Dialog>
      </Box>
    </Box>
  );
};

export default Home;